'use client';

import { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function FileUpload({
  label,
  required,
  accept = 'image/*',
  maxSize = 5 * 1024 * 1024, // 5MB
  error,
  value,
  onChange,
  className,
}) {
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;

    if (file.size > maxSize) {
      alert(`파일 크기는 ${maxSize / 1024 / 1024}MB 이하여야 합니다.`);
      return;
    }

    onChange?.(file);

    // 이미지 미리보기
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleChange = (e) => {
    handleFile(e.target.files?.[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleRemove = () => {
    onChange?.(null);
    setPreview(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
          {required && <span className="text-blue-500 ml-0.5">*</span>}
        </label>
      )}

      {(preview || value) ? (
        <div className="relative">
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className="w-full h-32 object-cover rounded-xl border border-slate-200"
            />
          ) : (
            <div className="w-full h-32 bg-slate-100 rounded-xl border border-slate-200 flex items-center justify-center">
              <span className="text-sm text-slate-500">{value?.name || '파일 선택됨'}</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label
          className={cn(
            'flex flex-col items-center justify-center w-full h-32',
            'border-2 border-dashed rounded-xl cursor-pointer',
            'transition-all duration-200 group',
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : error
              ? 'border-red-500 bg-red-50/50'
              : 'border-slate-200 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-300'
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <CloudArrowUpIcon
            className={cn(
              'w-8 h-8 mb-2 transition-colors',
              dragActive ? 'text-blue-500' : 'text-slate-400 group-hover:text-blue-500'
            )}
          />
          <p className="text-sm text-slate-500">파일 선택 또는 드래그</p>
          <p className="text-xs text-slate-400 mt-1">
            최대 {maxSize / 1024 / 1024}MB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
          />
        </label>
      )}

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}
